import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  inventoryApi,
  type CreateStockAlertRuleInput,
  type UpdateStockAlertRuleInput,
} from "@/lib/admin-api/inventory";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const ALERT_RULES_KEY = "stock-alert-rules" as const;

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Fetch paginated stock alert rules. */
export function useStockAlertRules(page = 1, limit = 20) {
  return useQuery({
    queryKey: [ALERT_RULES_KEY, page, limit],
    queryFn: () => inventoryApi.alerts.list({ page, limit }),
  });
}

/** Create a new stock alert rule. Invalidates list on success. */
export function useCreateAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStockAlertRuleInput) =>
      inventoryApi.alerts.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ALERT_RULES_KEY] });
    },
  });
}

/** Update an existing stock alert rule. Invalidates list on success. */
export function useUpdateAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateStockAlertRuleInput }) =>
      inventoryApi.alerts.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ALERT_RULES_KEY] });
    },
  });
}

/** Delete a stock alert rule. Invalidates list on success. */
export function useDeleteAlertRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => inventoryApi.alerts.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ALERT_RULES_KEY] });
    },
  });
}
